import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-covideo-common';
import { useTheme } from 'styled-components/macro';
import { useFormikContext } from 'formik';
import { IoArrowForward, IoEye } from 'react-icons/io5';
import { ButtonIconSwitch, LoadingIndicator, Switch } from 'lib/components';
import {
  ILandingPageBuilderForm,
  IUIKitFormData,
  LP_BUILDER_MODAL_STEP,
  LPBuilderSidebarItem,
} from '../../types';
import { LandingPageDesignPreview } from '../preview/LandingPageDesignPreview';
import { SaveUIKitAction } from '../uikit/UIKit';
import { Flex } from 'lib/components/styles/layout';
import { LandingPageBuilderForm } from '../form/LandingPageBuilderForm';
import { ModalHeader } from 'app/pages/guides/modals/VisibilityModal';
import {
  ParagraphNormalBold,
  ParagraphNormalBold600,
} from 'lib/components/styles/typography';
import {
  MdArrowBack,
  MdClose,
  MdLaptop,
  MdRefresh,
  MdSmartphone,
} from 'react-icons/md';
import {
  ControlContainer,
  HorizontalMenu,
  Sidebar,
  SidebarItem,
} from '../../styles';
import {
  IUIKitsQueryParams,
  IUIKitsResponse,
} from 'lib/api/superadmin/uikits/useQueryUIKits';
import LandingPageBuilderAccessForm from '../manageAccessForm/LandingPageBuilderAccessForm';
import {
  LP_BUILDER_FORM_MODE,
  LP_BUILDER_MODAL_STEPS,
  LpBuilderFormMode,
  useGetSidebarItems,
} from '../../constants';
import { GlobalFontStyles } from 'app/pages/design/landingPageBuilder/components/editSectionComponents/EditTextSection';
import {
  IResellersWithCustomers,
  useGetResellersWithCustomerCounts,
} from 'lib/api/superadmin/useGetResellerCustomerCounts';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query';
import { LandingPageDesignAccess } from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPageDesigns';
import { transformLandingPageDesignAccess } from '../../helpers/transformLandingPageDesignAccess';
import { useFontFamilyOptions } from '../../helpers/useFontFamilyOptions';
import { FormSection } from '../form/shared/FormSection';
import { LandingPageChangedFieldsSummary } from './summary/LandingPageChangedFieldsSummary';
import {
  areBulkValuesUnchanged,
  ILandingPageBuilderBulkEditFormData,
} from '../../helpers/getInitialLandingPageValuesForBulkEdit';

export const PREVIEWS = {
  desktop: 'desktop',
  mobile: 'mobile',
} as const;

export type PreviewType = (typeof PREVIEWS)[keyof typeof PREVIEWS];

export type SidebarItemProps = {
  isActive: boolean;
};

interface PreviewOption {
  value: PreviewType;
  icon: JSX.Element;
}

const previewOptions: PreviewOption[] = [
  {
    value: PREVIEWS.desktop,
    icon: <MdLaptop size={20} />,
  },
  {
    value: PREVIEWS.mobile,
    icon: <MdSmartphone size={20} />,
  },
];

interface ILandingPageBuilderModalProps {
  onClose: () => void;
  handleOpenBuilderPreview: (
    values: ILandingPageBuilderForm,
    showSalesContent: boolean
  ) => void;
  selectedTemplateDesignId: number | null;
  step: LP_BUILDER_MODAL_STEP;
  setStep: React.Dispatch<React.SetStateAction<LP_BUILDER_MODAL_STEP>>;
  isGeneratingAssets: boolean;
  isPreparingDataForPreview: boolean;
  isLoadingUIKits: boolean;
  uiKitsQueryParams: IUIKitsQueryParams;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<IUIKitsResponse, unknown>>;
  totalCount: number;
  pageTotal: number;
  isLoading: boolean;
  templateDesignAccess?: LandingPageDesignAccess[];
  setSearchUIKitsQuery: React.Dispatch<React.SetStateAction<string>>;
  searchUIKitsQuery: string;
  uiKitsResponse: IUIKitFormData[];
  mode: LpBuilderFormMode;
  groupTitle?: string;
}

export const buildImageField = (file: File | null, url: string) => ({
  file: file || null,
  url: url || '',
});

const INIT_SIZE = 500;

export const LandingPageBuilderModal = ({
  onClose,
  selectedTemplateDesignId,
  step,
  setStep,
  isGeneratingAssets,
  isLoadingUIKits,
  uiKitsQueryParams,
  fetchNextPage,
  totalCount,
  pageTotal,
  isLoading,
  templateDesignAccess,
  setSearchUIKitsQuery,
  searchUIKitsQuery,
  handleOpenBuilderPreview,
  isPreparingDataForPreview,
  uiKitsResponse,
  mode,
  groupTitle,
}: ILandingPageBuilderModalProps) => {
  const { values, setFieldValue, submitForm, isValid } = useFormikContext<
    ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  >();

  const areInitialBulkValuesUnchanged = () => {
    if (!isBulkEditTemplate) return false;
    return areBulkValuesUnchanged(
      values as ILandingPageBuilderBulkEditFormData
    );
  };

  useEffect(() => {
    setFieldValue('uiKits', uiKitsResponse);
  }, [uiKitsResponse]);

  const sidebarItems = useGetSidebarItems();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const themes = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const [saveAction, setSaveAction] = useState<SaveUIKitAction | null>(null);

  const initialSidebarItem = sidebarItems?.[0]?.items?.[0] ?? null;
  const [activeSidebarItem, setActiveSidebarItem] =
    useState<LPBuilderSidebarItem | null>(initialSidebarItem);

  // preview
  const [showLandingPagePreview, setShowLandingPagePreview] = useState(true);
  const [showSalesPreviewContent, setShowSalesPreviewContent] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState<PreviewType>(
    PREVIEWS.desktop
  );

  // UI Kit
  const [isUIKitCreate, setIsUIKitCreate] = useState(false);
  const [isUIKitUpdate, setIsUIKitUpdate] = useState(false);

  // resellers
  const [resellersPage, setResellersPage] = useState(0);
  const [resellersData, setResellersData] = useState<IResellersWithCustomers[]>(
    []
  );

  // font family options
  const defaultFonts = useMemo(() => [], []);
  const allFontFamilyOptions = useFontFamilyOptions(
    defaultFonts,
    mode === LP_BUILDER_FORM_MODE.BULK_EDIT
  );

  // GET Resellers
  const {
    data,
    isFetching: isFetchingResellers,
    isLoading: isLoadingResellers,
    refetch,
  } = useGetResellersWithCustomerCounts({
    limit: INIT_SIZE,
    start: resellersPage,
  });
  const resellers = data?.pages.map(page => page.data)[0] || [];
  const count = data?.pages[0]?.count || 0;

  // Populate `access` object with initial values
  useEffect(() => {
    if (isBulkEditTemplate) return;
    if (isLoading) return;
    if (resellers && resellers.length) {
      let combinedResellers;

      if (values.selectedResellerId !== -1) {
        combinedResellers = resellers;
      } else {
        const existingIds = new Set(resellersData.map(r => r.resellerId));
        const newUniqueResellers = resellers.filter(
          r => !existingIds.has(r.resellerId)
        );
        combinedResellers = [...resellersData, ...newUniqueResellers];
      }

      setResellersData(combinedResellers);

      // initial values for selected resellers and customers
      const init = transformLandingPageDesignAccess(
        selectedTemplateDesignId,
        templateDesignAccess
      );

      const formikAccessObject = combinedResellers.reduce((acc, curr) => {
        const resellerId = curr.resellerId;
        const valuesAccessEntry = (values as ILandingPageBuilderForm).access[
          resellerId
        ];
        const initialAccess = init?.[resellerId];

        return {
          ...acc,
          [resellerId]: {
            resellerName: valuesAccessEntry?.resellerName || curr.resellerName,
            isResellerSelected:
              valuesAccessEntry?.isResellerSelected ||
              initialAccess?.isResellerSelected ||
              false,
            totalCustomerCount:
              valuesAccessEntry?.totalCustomerCount ||
              Number(curr.customerCount || 0),
            selectedCustomers:
              valuesAccessEntry?.selectedCustomers ||
              initialAccess?.selectedCustomers ||
              {},
          },
        };
      }, {});

      setFieldValue('access', formikAccessObject);
    }
  }, [resellers, templateDesignAccess, isLoading]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };
  useEffect(() => {
    // scroll to top when active sidebar item changes
    scrollToTop();
  }, [activeSidebarItem, saveAction]);

  const isUIKitUpsertSelected = isUIKitCreate || isUIKitUpdate;
  const isEditTemplate = mode === LP_BUILDER_FORM_MODE.EDIT;
  const isBulkEditTemplate = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;

  const getStepTitle = (stepNumber: number): string => {
    if (isBulkEditTemplate) {
      return `Bulk Edit "${groupTitle}" — Step ${stepNumber}`;
    }

    if (isEditTemplate) {
      return `${values.title} — Step ${stepNumber}`;
    }

    return `Landing Page Builder — Step ${stepNumber}`;
  };

  const handleTransitionToSecondStep = async () => {
    const nextStep = isBulkEditTemplate
      ? LP_BUILDER_MODAL_STEPS.changesSummary
      : LP_BUILDER_MODAL_STEPS.manageAccess;
    setStep(nextStep);
  };

  const toggleSalesPreviewContent = () => {
    setShowSalesPreviewContent(prev => !prev);
  };

  return (
    <>
      <GlobalFontStyles fonts={allFontFamilyOptions} />
      {step === LP_BUILDER_MODAL_STEPS.design && (
        <>
          <ModalHeader>
            <Button
              variant='transparent'
              icon={<MdClose size='18px' />}
              onClick={onClose}
              disabled={isLoading}
            />
            {!isLoading && (
              <ParagraphNormalBold>{getStepTitle(1)}</ParagraphNormalBold>
            )}
            <Button
              variant='primary'
              text='Next'
              icon={<IoArrowForward />}
              iconPosition='right'
              type='submit'
              disabled={
                isGeneratingAssets ||
                isUIKitUpsertSelected ||
                areInitialBulkValuesUnchanged() ||
                isLoading ||
                isPreparingDataForPreview
              }
              onClick={handleTransitionToSecondStep}
            />
          </ModalHeader>

          {!isLoading ? (
            <>
              <HorizontalMenu
                style={{
                  padding: '0 24px',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
              >
                <Flex
                  flexDirection='row'
                  alignItems='center'
                  gap={'8px'}
                  width='fit-content'
                >
                  <div>Preview Sales Page Content</div>
                  <Switch
                    isOn={showSalesPreviewContent}
                    handleToggle={toggleSalesPreviewContent}
                    onColor={themes.colors.primary[100]}
                    id='sales-preview'
                    disabled={isUIKitUpsertSelected}
                  />
                </Flex>
                <Flex
                  flexDirection='row'
                  alignItems='center'
                  gap={'8px'}
                  width='fit-content'
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <ButtonIconSwitch
                    defaultValue={selectedPreview}
                    onChange={preview => {
                      setSelectedPreview(preview as PreviewType);
                    }}
                    values={previewOptions}
                    disabled={isUIKitUpsertSelected}
                  />
                </Flex>
                {!isBulkEditTemplate && (
                  <Flex
                    flexDirection='row'
                    alignItems='center'
                    gap={'8px'}
                    width='fit-content'
                  >
                    <Button
                      text='Preview'
                      variant='secondary'
                      icon={
                        isPreparingDataForPreview ? <MdRefresh /> : <IoEye />
                      }
                      onClick={() =>
                        handleOpenBuilderPreview(
                          values as ILandingPageBuilderForm,
                          showSalesPreviewContent
                        )
                      }
                      loading={isPreparingDataForPreview}
                      disabled={
                        isUIKitUpsertSelected || isPreparingDataForPreview
                      }
                    />
                  </Flex>
                )}
              </HorizontalMenu>

              <Flex flexDirection='row' height='100%' margin='2px 0 0 0'>
                {sidebarItems && (
                  <Sidebar>
                    {sidebarItems.map(category => (
                      <FormSection
                        title={category.title}
                        key={`lpbuilder-sidebar-category-${category.title}`}
                        extendStyles={{
                          container: { textTransform: 'none' },
                          wrapper: { gap: '8px' },
                        }}
                      >
                        {category.items.map(subItem => {
                          const Icon = subItem.icon;
                          const isActive =
                            activeSidebarItem?.name === subItem.name;

                          return (
                            <SidebarItem
                              key={`lpbuilder-sidebar-item-${subItem.name}`}
                              isActive={isActive}
                              onClick={() => setActiveSidebarItem(subItem)}
                            >
                              <Icon
                                size={20}
                                color={themes.colors.secondary[100]}
                                className='lp-sidebar-icon'
                              />
                              <ParagraphNormalBold600
                                color={themes.colors.secondary[100]}
                              >
                                {subItem.name}
                              </ParagraphNormalBold600>
                            </SidebarItem>
                          );
                        })}
                      </FormSection>
                    ))}
                  </Sidebar>
                )}
                <ControlContainer ref={scrollContainerRef}>
                  <LandingPageBuilderForm
                    mode={mode}
                    activeForm={activeSidebarItem?.name}
                    setShowLandingPagePreview={setShowLandingPagePreview}
                    params={uiKitsQueryParams}
                    setSearchUIKitsQuery={setSearchUIKitsQuery}
                    searchUIKitsQuery={searchUIKitsQuery}
                    setSaveAction={setSaveAction}
                    saveAction={saveAction}
                    fetchNextPage={fetchNextPage}
                    totalCount={totalCount}
                    pageTotal={pageTotal}
                    isLoadingUIKits={isLoadingUIKits}
                    allFontFamilyOptions={allFontFamilyOptions}
                    setIsUIKitCreate={setIsUIKitCreate}
                    isUIKitCreate={isUIKitCreate}
                    setIsUIKitUpdate={setIsUIKitUpdate}
                    isUIKitUpdate={isUIKitUpdate}
                    scrollToTop={scrollToTop}
                  />
                </ControlContainer>
                <LandingPageDesignPreview
                  ref={previewRef}
                  preview={selectedPreview}
                  showSalesPreviewContent={showSalesPreviewContent}
                  showLandingPagePreview={showLandingPagePreview}
                />
              </Flex>
            </>
          ) : (
            <div>
              <LoadingIndicator isLoading={true} />
            </div>
          )}
        </>
      )}

      {step === LP_BUILDER_MODAL_STEPS.manageAccess && (
        <>
          <ModalHeader>
            <Button
              variant='transparent'
              icon={<MdArrowBack size='18px' />}
              onClick={() => {
                setStep(LP_BUILDER_MODAL_STEPS.design);
              }}
              disabled={isLoading}
            />
            {!isLoading && (
              <ParagraphNormalBold>{getStepTitle(2)}</ParagraphNormalBold>
            )}
            <Button
              variant='primary'
              text={isEditTemplate ? 'Update' : 'Publish'}
              onClick={submitForm}
              type='submit'
              disabled={isLoading || isGeneratingAssets || !isValid}
            />
          </ModalHeader>
          {!isLoading ? (
            <>
              <HorizontalMenu
                style={{
                  height: '48px',
                  padding: '0 125px 0 55px',
                  justifyContent: 'center',
                }}
              >
                <Flex
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='center'
                >
                  <ParagraphNormalBold color={themes.colors.primary[100]}>
                    Assign Template
                  </ParagraphNormalBold>
                </Flex>
              </HorizontalMenu>

              <Flex width='unset' alignItems='center' padding='35px 0 0 0'>
                <LandingPageBuilderAccessForm
                  resellers={resellers}
                  count={count}
                  refetch={refetch}
                  isFetching={isFetchingResellers || isLoadingResellers}
                  page={resellersPage}
                  setPage={setResellersPage}
                  resellersData={resellersData}
                  setResellersData={setResellersData}
                />
              </Flex>
            </>
          ) : (
            <div>
              <LoadingIndicator isLoading={true} />
            </div>
          )}
        </>
      )}

      {step === LP_BUILDER_MODAL_STEPS.changesSummary && (
        <>
          <ModalHeader>
            <Button
              variant='transparent'
              icon={<MdArrowBack size='18px' />}
              onClick={() => {
                setStep(LP_BUILDER_MODAL_STEPS.design);
              }}
              disabled={isLoading}
            />
            {!isLoading && (
              <ParagraphNormalBold
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                {getStepTitle(2)}
              </ParagraphNormalBold>
            )}
            <Button
              variant='primary'
              text={'Apply Changes To Group'}
              onClick={submitForm}
              type='submit'
              disabled={isLoading || isGeneratingAssets || !isValid}
            />
          </ModalHeader>
          {!isLoading ? (
            <>
              <HorizontalMenu
                style={{
                  height: '48px',
                  justifyContent: 'center',
                }}
              >
                <Flex
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='center'
                >
                  <ParagraphNormalBold color={themes.colors.primary[100]}>
                    Changes Summary
                  </ParagraphNormalBold>
                </Flex>
              </HorizontalMenu>
              <Flex alignItems='center' padding='30px 0px' gap='30px'>
                <div>
                  Changes below will be applied to all landing pages in{' '}
                  <span style={{ fontWeight: '700' }}>"{groupTitle}":</span>
                </div>
                <Flex
                  style={{
                    width: '290px',
                    overflowY: 'auto',
                    height: 'calc(100vh - 260px)',
                  }}
                  gap='32px'
                >
                  <LandingPageChangedFieldsSummary
                    mode={mode}
                    values={values as ILandingPageBuilderBulkEditFormData}
                  />
                </Flex>
              </Flex>
            </>
          ) : (
            <div>
              <LoadingIndicator isLoading={true} />
            </div>
          )}
        </>
      )}
    </>
  );
};
